import React from "react";
import {
  Crosshair,
  Eye,
  Glasses,
  ShieldCheck,
  ChevronRight,
} from "lucide-react";
import styles from "./PublicationHero.module.css";

const STAT_ICONS = { crosshair: Crosshair, eye: Eye, glasses: Glasses, shield: ShieldCheck };

/*
  PublicationHero — "Landmark Clinical Study Published" page.
  Left: eyebrow + title + copy + stat cards + CTA + footnotes.
  Right: contact-lens visual with a faint radar/blueprint graphic.
  Theme accent: #00a7e1. Fully responsive.
*/
const PublicationHero = ({
  eyebrow = "New Publication",
  title = ["Landmark Clinical", "Study Published"],
  stats = [
    { icon: "crosshair", value: "+0.78 D", label: "Depth of focus" },
    { icon: "eye", value: "~1.6 lines", label: "Near vision" },
    { icon: "glasses", value: "~1.4 lines", label: "Intermediate vision" },
    { icon: "shield", value: "No adverse events", label: "Reported during the study" },
  ],
  ctaLabel = "Read the Full Study",
  studyUrl = "https://www.businesswire.com/news/home/20260621383692/en/The-Cataltheia-Group-and-Bruno-Vision-Care-Announce-Publication-of-Landmark-Clinical-Study-Validating-Deseyne-Extended-Depth-of-Focus-Technology-for-Presbyopia?utm_campaign=shareaholic&utm_medium=linkedin&utm_source=socialnetwork",
  lensSrc = "/Images/why_lense img2.png",
}) => {
  return (
    <div className={styles.pub}>
      <div className={styles.inner}>
        {/* Left: copy */}
        <div className={styles.content}>
          <span className={styles.eyebrow}>{eyebrow}</span>

          <h1 className={styles.title}>
            {title.map((line, i) => (
              <span className={styles.titleLine} key={i}>
                {line}
              </span>
            ))}
          </h1>

          <p className={styles.intro}>
            A peer-reviewed study published in <em>Optometry and Vision Science</em> validates
            Deseyne Extended Depth of Focus technology for presbyopia.
          </p>
          <p className={styles.desc}>
            The publication demonstrated improvements in depth of focus, near vision, and
            intermediate vision while maintaining distance vision performance.
          </p>

          <div className={styles.statGrid}>
            <span className={styles.statHead}>Study highlights</span>
            {stats.map((s, i) => {
              const Icon = STAT_ICONS[s.icon] || Crosshair;
              return (
                <div className={styles.statCard} key={i}>
                  <span className={styles.statIcon}>
                    <Icon size={20} strokeWidth={1.8} />
                  </span>
                  <span className={styles.statText}>
                    <span className={styles.statValue}>{s.value}</span>
                    <span className={styles.statLabel}>{s.label}</span>
                  </span>
                </div>
              );
            })}
          </div>

          <div className={styles.ctaRow}>
            <a href={studyUrl} target="_blank" rel="noreferrer" className={styles.cta}>
              {ctaLabel}
              <ChevronRight size={18} strokeWidth={2.4} />
            </a>
          </div>

        </div>

        {/* Right: lens visual + radar graphic */}
        <div className={styles.visual} aria-hidden="true">
          <span className={styles.radar} />
          {lensSrc && <img src={lensSrc} alt="lensSrc" className={styles.lens} />}
        </div>
      </div>
    </div>
  );
};

export default PublicationHero;
